import {
  observable,
  action,
  computed,
  toJS,
} from 'mobx';
import moment from 'moment-timezone';
import { toNumber } from '../utils/numbers';
import convertUnit from '../utils/unit-converter';
import {
  Measurement,
  ToleranceMode,
} from '../constants/Configuration';
import logger from '../utils/logger';
import getTicketTemplate from '../containers/Tickets/template-ticket';
import { WebsocketEvent } from '../constants/WebsocketEvent';
import { inflate } from 'pako/dist/pako_inflate.es5';
import {
  BatchMode,
  ConstituentType,
  WeighedMaterialType,
} from '../constants/MaterialModels';
import {
  asyncForEach,
  waitFor,
} from '../utils/async';
import { OrderOrigin } from '../constants/OrderOrigin';
import { generateBarcodeImage } from '../utils/barcode';

import { calculateIceAdjustment } from '../containers/Batch/utils/calculate-ice-adjustment';
import { getAverageMoisture } from './batch/live-moisture';
import { displayDate } from '../utils/date';

class TicketStore {
  @observable
    tickets = [];
  serverTickets = [];
  @observable
    ticketsDateFrom = moment.tz(this.timezone).startOf('day');
  @observable
    ticketsDateTo = moment.tz(this.timezone).endOf('day');
  @observable
    simpleTickets = [];
  @observable
    simpleTicketsDateFrom = moment.tz(this.timezone).startOf('day');
  @observable
    simpleTicketsDateTo = moment.tz(this.timezone).endOf('day');
  @observable
    templates = [];
  @observable
    templateId = '';
  @observable
    template = '';
  @observable
    batchingLogs = [];
  @observable
    activeLog = null;
  @observable
    numberOfCopies = 1;
  @observable
    termsTemplate = '';

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  get api() {
    return this.rootStore.newAuthStore;
  }

  get plantId() {
    return this.rootStore.locationsStore.plantId;
  }

  get timezone() {
    return this.rootStore.settingsStore.configuration.ianaTimeZone;
  }

  get scheduleStore() {
    return this.rootStore.scheduleStore;
  }

  get materialStore() {
    return this.rootStore.materialStore;
  }

  @computed
  get offlineTicketNameSpace() {
    return 'offlineTickets';
  }

  @computed
  get offlineTicketsFromDispatchNameSpace() {
    return 'offlineTicketsFromDispatch';
  }

  loadOfflineTicketsFromDispatch() {
    return this.api.storeDB.init()
      .then(() => this.api.storeDB.getByKey(this.offlineTicketsFromDispatchNameSpace))
      .then(data => {
        return this.offlineTicketsFromDispatch = data || [];
      });
  }

  @action
  merge(dispatchTicketId, localTicketId) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/batchTicket/${dispatchTicketId}/merge/${localTicketId}`, {
        dispatchTicketId,
        localTicketId,
      },'PUT');
  };

  @action
  unmerge(dispatchTicketId) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/batchTicket/${dispatchTicketId}/unmerge`, {
        dispatchTicketId,
      },'PUT');
  };

  @action
  loadTickets() {
    const url = `/api/plant/${this.plantId}/batchTicket`;
    return this.api
      .makeAuthorizedRequest(url,
        {
          skip: 0,
          take: 1000,
          'loadStartTime.from': moment(this.ticketsDateFrom).tz(this.timezone).format(),
          'loadStartTime.to': moment(this.ticketsDateTo).tz(this.timezone).format(),
        })
      .then(resp => Promise.all([
        this.api.storeDB.init().then(() => this.api.storeDB.getByKey(this.offlineTicketNameSpace)),
        this.api.storeDB.init().then(() => this.api.storeDB.getByKey(this.scheduleStore.offlineOrdersNameSpace)),
        this.loadOfflineTicketsFromDispatch(),
      ]).then(([offlineTicketData, offlineOrderData, dispatchTicketData]) => {
        const offlineTickets = offlineTicketData || [];
        const offlineOrders = offlineOrderData || [];
        const localTickets = offlineTickets.slice();

        offlineOrders.forEach(order => {
          if (order.tickets?.length && !order.dispatchOrderId) {
            localTickets.push(...order.tickets);
          }
          if (order.ticket) {
            localTickets.push(order.ticket);
          }
        });

        dispatchTicketData.forEach(order => {
          if (order.tickets?.length) {
            localTickets.push(...order.tickets);
          }
        });

        const localTicketIds = new Set(localTickets.map(ticket => ticket.id));
        const previousTicketsById = new Map(
          [...this.serverTickets, ...this.tickets]
            .filter(ticket => ticket.id != null)
            .map(ticket => [ticket.id, ticket]),
        );

        if (this.rootStore.connectionStore.isOnline || this.serverTickets.length === 0) {
          this.serverTickets = (resp || [])
            .filter(ticket => !ticket.isOffline && !localTicketIds.has(ticket.id))
            .map(ticket => ({
              ...previousTicketsById.get(ticket.id),
              ...ticket,
            }));
        }

        const ticketsById = new Map();
        const tickets = [];
        [...this.serverTickets, ...localTickets].forEach(ticket => {
          const existingTicket = ticket.id != null && ticketsById.get(ticket.id);
          if (existingTicket) {
            Object.assign(existingTicket, ticket);
            return;
          }

          const mergedTicket = {
            ...previousTicketsById.get(ticket.id),
            ...ticket,
          };
          tickets.push(mergedTicket);
          if (ticket.id != null) {
            ticketsById.set(ticket.id, mergedTicket);
          }
        });

        this.tickets = tickets;
        return this.loadBatchingLogs().then(() => this.tickets);
      }));
  };

  @action
  loadSimpleTickets(options) {
    const url = `/api/plant/${this.plantId}/deviceBatching`;
    const params = options || {
      skip: 0,
      take: 1000,
      'date.from': moment(this.simpleTicketsDateFrom).tz(this.timezone).format(),
      'date.to': moment(this.simpleTicketsDateTo).tz(this.timezone).format(),
    };

    return this.api
      .makeAuthorizedRequest(url, params)
      .then(resp => {
        if (options) {
          return resp;
        }

        this.simpleTickets = resp || [];
      });
  };

  @action
  loadBatchingLogs() {
    const url = `/api/plant/${this.plantId}/log`;
    return this.api
      .makeAuthorizedRequest(url,
        {
          pageSize: 500,
          pageNumber: 1,
          'batchingDate.from': moment(this.ticketsDateFrom).tz(this.timezone).format(),
          'batchingDate.to': moment(this.ticketsDateTo).tz(this.timezone).format(),
        })
      .then(resp => {
        this.batchingLogs = resp || [];
        return this.batchingLogs;
      });
  };

  @action
  loadBatchingLog(fileName = '', onData = () => {}) {
    const { notifierStore } = this.rootStore;

    if (!fileName) {
      return Promise.reject(new Error('No file name provided'));
    }

    const url = `/api/plant/${this.plantId}/log/${fileName}`;
    return this.api
      .makeAuthorizedRequest(
        url,
        null,
        'GET',
      )
      .then(response => {
        const reader = response.body.getReader();

        return new ReadableStream({
          start(controller) {
            return pump();
            function pump() {
              return reader.read().then(({ done, value }) => {
                if (done) {
                  controller.close();
                  return;
                }
                controller.enqueue(value);
                return pump();
              });
            }
          },
        });
      })
      .then(stream => new Response(stream))
      .then(response => response.blob())
      .then(blob => {
        logger.log('compressed batch log data size', blob.size);

        if (blob.size > 50000000) {
          logger.log('history batch log data is too large!', blob.size);
          notifierStore.showWarning('The history data for this ticket is too large to display!');
          onData();
          return;
        } else if (!blob.size) {
          notifierStore.showWarning('No history data for this ticket!');
          onData();
          return;
        }

        const reader = new FileReader();

        reader.addEventListener('loadend', (e) => {

          try {
            let data = inflate(new Uint8Array(e.target.result), { to: 'string', chunkSize: 1024*1024*1024 });
            data = JSON.parse(`[${data.replaceAll('}{', '},{')}]`);
            logger.log('uncompressed batch log data size', data.length);

            if (data.length > 2000) {
              data.splice(2000, data.length);
              logger.log('uncompressed batch log data size', data.length);
            }

            onData(data);

            return data;
          } catch (err) {
            logger.error(err);
            onData();
            notifierStore.showWarning(`Couldn't uncompress the data from batch log: ${err}`);
          }

        });

        reader.readAsArrayBuffer(blob);

      })
      .catch(err => {
        logger.error(err);
        notifierStore.showWarning(`Couldn't fetch the data from batch log: ${err}`);
      });
  };

  @action
  createSimpleTicket(payload) {
    const { isDemo } = this.rootStore.batchStore;
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/deviceBatching`, {
        date: moment().tz(this.timezone).format(),
        quantity: payload.quantity,
        deviceId: payload.deviceId,
        deviceDescription: payload.deviceDescription,
        itemId: payload.itemId,
        itemCode: payload.itemCode,
        unitId: payload.unitId,
        unitSymbol: payload.unitSymbol,
        isDemo,
      },'POST')
      .then(resp => {
        this.simpleTickets.push(resp);
      });
  };

  @action
  updateSimpleTicket(payload) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/deviceBatching/${payload.id}`, {
        itemId: payload.itemId,
        itemCode: payload.itemCode,
        batchTicketId: payload.batchTicketId,
      },'PUT')
      .then(ticket => {
        this.loadSimpleTickets();
        return ticket;
      });
  };

  @action
  createTicketOnBatchStart(payload) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/ticket/${payload.id}/batchTicket/create`, {
        loadStartTime: payload.loadStartTime || moment().tz(this.timezone).format(),
        mixQuantity: payload.quantityToBeBatched || payload.mixQuantity,
        mixQuantityInTruck: payload.mixQuantityInTruck,
        // Note: Backend names these values as `quantity`, so we need to map them accordingly
        mixBatchQuantity: payload.batchSize,
        mixDropQuantity: payload.dropSize,
        truckId: payload.truckId,
        driverId: payload.driverId,
        projectId: payload.projectId,
        printWithoutWeights: !!payload.printWithoutWeights,
        printWithoutWeightsAtEnd: !!payload.printWithoutWeightsAtEnd,
        printWithWeights: !!payload.printWithWeights,
        printOnlyWeights: !!payload.printOnlyWeights,
        waterInTruck: payload.waterInTruck,
        adjustedWater: payload.adjustedWater,
        ice: payload.ice,
        iceUnit: payload.iceUnit,
        isDemo: !!payload.isDemo,
        isTestMode: !!payload.isTestMode,
        runMode: payload.runMode,
        results: payload.results || [],
      },'POST')
      .then(resp => {
        this.tickets.push(resp);
        this.scheduleStore.loadSchedules();
        return resp;
      });
  };

  saveTicketsOffline() {
    this.api.storeDB.init()
      .then(() => {
        const ticketsById = new Map(this.tickets.map(ticket => [ticket.id, ticket]));
        this.serverTickets = this.serverTickets.map(ticket => ({
          ...ticket,
          ...ticketsById.get(ticket.id),
        }));
        return this.api.storeDB.save(`/api/plant/${this.plantId}/batchTicket`, toJS(this.serverTickets));
      });
  }

  @action
  updateTicketOnBatchEnd(payload) {
    const data = {
      loadStartTime: payload.loadStartTime,
      loadEndTime: payload.loadEndTime || moment().tz(this.timezone).format(),
      manualLoad: !!payload.manualLoad,
      temperWater: payload.temperWater,
      temperWaterUnitSymbol: payload.temperWaterUnitSymbol,
      temperWaterUnitId: payload.temperWaterUnitId,
      results: payload.results,
      drops: payload.drops,
      dropTimes: payload.dropTimes,
      isAborted: !!payload.isAborted,
      mixingTime: payload.mixingTime,
      dischargeSequenceId: payload.dischargeSequenceId,
      events: payload.events,
    };

    logger.info('ticket results:', payload.id, data);

    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/batchTicket/${payload.id}/end`, data,'PUT')
      .then(resp => {
        this.scheduleStore.loadSchedules();
        const index = this.tickets.findIndex(t => t.id === payload.id);
        if (index > -1) {
          this.tickets[index] = { ...this.tickets[index], ...resp };
          this.saveTicketsOffline();
        }
        return resp;
      })
      .catch(e => {
        if (e.status !== 409) {//save ticket only if not isVoided
          this.updateTicketOnBatchEndOffline({
            ...payload,
            ...data,
            mixDesign: [],
            isOffline: true,
          });
        }
        throw e;
      });
  };

  @action
  revertTicketFromQueue(batchTicketId) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/batchTicket/${batchTicketId}/revert`,
        null,
        'DELETE')
      .then(() => {
        return this.scheduleStore.loadSchedules();
      });
  }

  @action
  voidTicket(ticketId, reasonId, clearInventory = false) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/batchTicket/${ticketId}/void?reasonId=${reasonId}&clearInventory=${clearInventory}`,
        null,
        'DELETE');
  }

  @action
  createLocalOrderOnBatchStart(payload) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/batchOrder/${payload.id}/batchTicket/create`, {
        loadStartTime: moment().tz(this.timezone).format(),
        mixQuantity: payload.quantityToBeBatched,
        mixQuantityInTruck: payload.mixQuantityInTruck,
        // Note: Backend names these values as `quantity`, so we need to map them accordingly
        mixBatchQuantity: payload.batchSize,
        mixDropQuantity: payload.dropSize,
        truckId: payload.truckId,
        driverId: payload.driverId,
        projectId: payload.projectId,
        printWithoutWeights: !!payload.printWithoutWeights,
        printWithoutWeightsAtEnd: !!payload.printWithoutWeightsAtEnd,
        printWithWeights: !!payload.printWithWeights,
        printOnlyWeights: !!payload.printOnlyWeights,
        waterInTruck: payload.waterInTruck,
        adjustedWater: payload.adjustedWater,
        ice: payload.ice,
        iceUnit: payload.iceUnit,
        code: payload.code,
        isDemo: !!payload.isDemo,
        isTestMode: !!payload.isTestMode,
        runMode: payload.runMode,
      },'POST')
      .then(resp => {
        this.tickets.push(resp);
        this.scheduleStore.loadSchedules();
        return resp;
      });
  };

  @action
  updateOfflineTickets(data) {
    this.loadOfflineTicketsFromDispatch()
      .then(offlineTickets => {
        const offlineTicketsFromDispatch = offlineTickets || [];
        const orderIndex = offlineTicketsFromDispatch.findIndex(v => v.id === data.id);
        data = toJS({ ...data, isOffline: true }, {
          recurseEverything: true,
          exportMapsAsObjects: true,
          detectCycles: true,
        });

        if (orderIndex > -1) {
          const tickets = offlineTicketsFromDispatch[orderIndex].tickets;
          if (Array.isArray(tickets)) {
            offlineTicketsFromDispatch[orderIndex] = {
              ...offlineTicketsFromDispatch[orderIndex],
              tickets: [
                ...tickets,
                ...data.tickets,
              ],
            };
          } else {
            offlineTicketsFromDispatch[orderIndex] = data;
          }
        } else {
          offlineTicketsFromDispatch.push(data);
        }

        logger.log('offlineTicketsFromDispatch', offlineTicketsFromDispatch);
        this.api.storeDB.init()
          .then(() => this.api.storeDB.save(this.offlineTicketsFromDispatchNameSpace, toJS(offlineTicketsFromDispatch)));
      });
  }

  /**
   * Sync tickets from dispatch started offline
   */
  @action
  syncOfflineTicketsFromDispatch() {
    const { notifierStore } = this.rootStore;

    this.loadOfflineTicketsFromDispatch()
      .then(data => {
        let offlineTicketsFromDispatch = data || [];
        logger.log('sync offline tickets from dispatch started offline', offlineTicketsFromDispatch);

        const start = async () => {
          await asyncForEach(offlineTicketsFromDispatch, async (data, i) => {
            if (data.tickets && data.tickets.length) {
              data.tickets.forEach(t => {
                this.createTicketOnBatchStart(t)
                  .then((resp) => {
                    this.updateTicketOnBatchEnd({ ...t, id: resp.id })
                      .then(() => {
                        offlineTicketsFromDispatch[i].tickets = offlineTicketsFromDispatch[i].tickets.filter(v => v.id !== t.id);

                        if (!offlineTicketsFromDispatch[i].tickets.length) {
                          offlineTicketsFromDispatch = offlineTicketsFromDispatch.filter(v => v.id !== data.id);
                        }

                        this.api.storeDB.init()
                          .then(() => this.api.storeDB.save(this.offlineTicketsFromDispatchNameSpace, toJS(offlineTicketsFromDispatch)));
                      })
                      .catch(e => {
                        logger.warn(`Couldn't update offline ticket #${resp.id}! ${e.message}`);
                        notifierStore.showWarning(`Couldn't update offline ticket #${resp.id}! ${e.message}`);
                      });
                  })
                  .catch(e => {
                    logger.warn(`Couldn't save offline ticket #${t.id}! ${e.message}`);
                    notifierStore.showWarning(`Couldn't save offline ticket #${t.id}! ${e.message}`);
                  });
              });
            }
            logger.log('sync offline ticket', i, data.id);
            await waitFor(200);
          });
        };

        start()
          .then(() => {
            this.api.storeDB.init()
              .then(() => this.api.storeDB.save(this.offlineTicketsFromDispatchNameSpace, []));
          });
      });
  }

  @action
  syncOfflineTickets() {
    const { connectionStore, notifierStore } = this.rootStore;
    const { isOnline } = connectionStore;

    if (!isOnline || !this.plantId) {
      return;
    }

    this.api.storeDB.init()
      .then(() => this.api.storeDB.getByKey(this.offlineTicketNameSpace))
      .then(data => {
        let offlineTickets = data || [];

        logger.log('sync/end offline tickets', offlineTickets.length);

        if (!offlineTickets.length) {
          return;
        }

        offlineTickets.forEach(ticket => {
          this.updateTicketOnBatchEnd(ticket)
            .then((ticket) => {
              offlineTickets = offlineTickets.filter(v => v.id !== ticket.id);
              this.api.storeDB.init()
                .then(() => this.api.storeDB.save(this.offlineTicketNameSpace, toJS(offlineTickets)));
              notifierStore.showInfo(`Offline ticket #${ticket.id} saved!`);
            })
            .catch(e => {
              if (e.status === 409) {//if ticket isVoided remove offline ticket
                offlineTickets = offlineTickets.filter(v => v.id !== ticket.id);
                this.api.storeDB.init()
                  .then(() => this.api.storeDB.save(this.offlineTicketNameSpace, toJS(offlineTickets)));
              }
              logger.warn(e, ticket);
            });
        });
      });

  }

  @action
  updateTicketOnBatchEndOffline(payload) {
    this.api.storeDB.init()
      .then(() => this.api.storeDB.getByKey(this.offlineTicketNameSpace))
      .then(data => {
        let offlineTickets = data || [];
        const existingTicket = offlineTickets.find(v => v.id !== payload.id);
        const orderDeliveredMixQuantity = existingTicket ? existingTicket.orderDeliveredMixQuantity + payload.mixQuantity : payload.mixQuantity;
        offlineTickets = offlineTickets.filter(v => v.id !== payload.id);
        offlineTickets.push(toJS({ ...payload, orderDeliveredMixQuantity }, {
          exportMapsAsObjects: true,
          detectCycles: true,
          recurseEverything: true,
        }));
        this.api.storeDB.init()
          .then(() => this.api.storeDB.save(this.offlineTicketNameSpace, toJS(offlineTickets)));
      });
  };

  @action
  startBatchTicket(batchTicketId) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/batchTicket/${batchTicketId}/start`,
        {
          loadStartTime: moment().tz(this.timezone).format(),
        },
        'PUT')
      .then(resp => {
        this.scheduleStore.loadSchedules();
        return resp;
      });
  }

  @action
  createTicketAndLocalOrder(payload) {
    return this.api
      .makeAuthorizedRequest(`/api/plant/${this.plantId}/localOrder/batchTicket/create`, {
        date: moment().tz(this.timezone).format(),
        projectId: payload.projectId,
        projectName: payload.projectName,
        projectCode: payload.projectCode,
        customerId: payload.customerId,
        customerCode: payload.customerCode,
        customerName: payload.customerName,
        orderMixQuantity: payload.quantityToBeBatched,
        // Note: Backend names these values as `quantity`, so we need to map them accordingly
        mixBatchQuantity: payload.batchSize,
        mixDropQuantity: payload.dropSize,
        mixSlump: payload.slump,
        products: [{
          ...payload.mix,
          isMix: true,
          unitPrice: payload.mix.price,
          quantity: payload.quantityToBeBatched,
          batchQuantity: payload.batchSize,
          dropQuantity: payload.dropSize,
          totalQuantityPrice: payload.mix.price * payload.quantityToBeBatched,
          totalQuantityTax: payload.mix.totalQuantityTax,
        }],
        printWithoutWeights: !!payload.printWithoutWeights,
        printWithoutWeightsAtEnd: !!payload.printWithoutWeightsAtEnd,
        printWithWeights: !!payload.printWithWeights,
        printOnlyWeights: !!payload.printOnlyWeights,
        ticket: {
          loadStartTime: moment().tz(this.timezone).format(),
          driverId: payload.driverId,
          driverCode: payload.driverCode,
          driverName: payload.driverName,
          truckId: payload.truckId,
          truckCode: payload.truckCode,
          mixQuantity: payload.quantityToBeBatched,
          mixQuantityInTruck: payload.mixQuantityInTruck,
          waterInTruck: payload.waterInTruck,
          adjustedWater: payload.adjustedWater,
          ice: payload.ice,
          iceUnit: payload.iceUnit,
          code: payload.code,
          isDemo: !!payload.isDemo,
          isTestMode: !!payload.isTestMode,
          runMode: payload.runMode,
        },
      },'POST')
      .then(resp => {
        if (resp?.ticket?.id) {
          this.tickets.push(resp.ticket);
        }
        this.scheduleStore.loadSchedules();
        return resp;
      });
  };

  @action
  getTicketDetails(id) {
    const { isOnline } = this.rootStore.connectionStore;

    if (!isOnline) {
      const ticket = this.tickets.find((ticket) => ticket.id === id);
      if (ticket) {
        return Promise.resolve(ticket);
      }
    }

    return this.api.makeAuthorizedRequest(`/api/plant/${this.plantId}/batchTicket/${id}`);
  }

  getSubTotal(ticket) {
    let subTotal = 0;

    if (!ticket || !ticket.products) {
      return subTotal;
    }

    ticket.products.forEach(p => {
      subTotal += Number(p.quantity || 0) * Number(p.unitPrice || 0);
    });

    ticket.subTotal = subTotal;

    return subTotal;
  };

  getTaxTotal(ticket) {
    let taxAmount = 0;

    if (!ticket || !ticket.products) {
      return taxAmount;
    }

    ticket.products.forEach(p => {
      if ((p.isTaxable || ticket.origin === OrderOrigin.External) && p.totalQuantityTax) {
        taxAmount += Number(p.totalQuantityTax);
      }
    });

    return taxAmount;
  };

  getUnitSymbol(unitId) {
    return this.materialStore.getUnitSymbolByUnitId(unitId);
  }

  getBatchTime(ticket) {
    if (!ticket?.loadStartTime || !ticket?.loadEndTime) {
      return '';
    }

    const diff = moment(ticket.loadEndTime).diff(ticket.loadStartTime, 'seconds');
    const minutes = Math.floor(Number(diff / 60));
    const seconds = diff - (minutes * 60);

    return ` ${minutes} min ${seconds} sec`;
  }

  getTotalUnit() {
    const { configuration } = this.rootStore.settingsStore;
    return configuration && configuration.measurement === Measurement.Metric ? 'M3' : 'Y3';
  }

  getUnit() {
    const { configuration } = this.rootStore.settingsStore;
    return configuration && configuration.measurement === Measurement.Metric ? 'kg' : 'lb';
  }

  getWaterUnit() {
    const { configuration } = this.rootStore.settingsStore;
    return configuration && configuration.measurement === Measurement.Metric ? 'l' : 'ga';
  }

  getWaterPercentForResultItem(data) {
    const { materialStore } = this.rootStore;
    const material = data.batchCode
      ? materialStore.getMaterialByBatchCode(data.batchCode)
      : materialStore.getMaterialByItemId(Number(data.itemId));

    return this.getWaterPercent(material);
  }

  getWaterPercent(material) {
    const { materialStore } = this.rootStore;
    const md = material ? materialStore.materialDetails[material.materialDetailId] || {} : {};

    return md.type === WeighedMaterialType.Water || md.type === WeighedMaterialType.SecondWater
      ? 100 : md.waterPercent || 0;
  }

  getActualWater(data, isTargetWater = false, deductAbsorption = false) {
    const unitFrom = this.getUnitSymbol(data.unitId);
    const unitTo = this.getWaterUnit();
    const val = isTargetWater ? data.target : data.actual;

    if (data.type === ConstituentType.WATER) {
      return convertUnit(val, unitFrom, unitTo);
    }

    if (data.type === ConstituentType.ADMIXTURE) {
      return convertUnit(val * (this.getWaterPercentForResultItem(data) / 100), unitFrom, unitTo);
    }

    if (!data.moisture) {
      return 0;
    }

    if (deductAbsorption && data.absorption) {
      return convertUnit(val - val / ( 1 + (data.moisture-data.absorption) / 100), unitFrom, unitTo);
    }

    return convertUnit(val - val / (1 + data.moisture / 100), unitFrom, unitTo);
  }

  getFreeMsc(data) {
    return toNumber((((data.moisture || 0) - (data.absorption || 0)) / 100) * (data.mix + data.trim), 0);
  }

  getSsd(data) {
    return toNumber(((( (data.absorption || 100)) / 100) * (data.mix + data.trim)) + (data.mix + data.trim), 0);
  }

  getTargetWater(data) {
    return this.getActualWater(data, true);
  }

  getTargetWaterLbKg(data) {
    return toNumber(convertUnit(this.getTargetWater(data), this.getWaterUnit(), this.getUnit()), 2);
  }

  getActualFreeWater(data = {}){
    const moisture = data.moisture || this.getWaterPercentForResultItem(data) || 0;
    const absorption = data.absorption || 0;

    if (data.type === ConstituentType.WATER) {
      return convertUnit(data.actual, data.unitSymbol, this.getUnit());
    }

    if (data.type === ConstituentType.ADMIXTURE) {
      return convertUnit(data.actual * (this.getWaterPercentForResultItem(data) / 100), data.unitSymbol, this.getUnit());
    }

    return convertUnit(data.actual - data.actual / ( 1 + (moisture - absorption) / 100), data.unitSymbol, this.getUnit());
  }

  getWaterAllowedByDesign(data = {}){
    return toNumber(convertUnit(this.getDesignWater(data) * data.mixQuantity, this.getWaterUnit(), this.getUnit()));
  }

  getWaterAllowedToAddByMix(data = {}){
    const mix = data.products?.find(v => v.isMix);
    const maxWater = data.maxWater || mix?.maxWater || 0;
    return maxWater ? toNumber((maxWater * data.mixQuantity) - this.getTotalWater(data, true), 2) : 0;
  }

  getWaterAllowedToAdd(data = {}){
    return toNumber((this.getDesignWater(data) * data.mixQuantity) - this.getTotalWater(data, true), 2);
  }

  getTotalWater(data = {}, deductAbsorption = false) {
    let total = 0;
    const mixQuantity = data.mixQuantity || data.quantityToBeBatched;
    const iceAdjustment = calculateIceAdjustment(data.ice, data.iceUnit, mixQuantity, this.getWaterUnit(), true);
    const waterInTruck = Number(data.waterInTruck || 0);

    if (data.results) {
      data.results.forEach(v => {
        const actualWater = this.getActualWater(v, false, deductAbsorption);
        total += actualWater;
        logger.log('actual', v.type, v.name, v.actual, v.unitSymbol, 'free water:', actualWater, this.getWaterUnit());
      });
    }

    const temperWater = data.temperWater ? convertUnit(data.temperWater, data.temperWaterUnitSymbol, this.getWaterUnit()) : 0;
    const totalWater = total + iceAdjustment + temperWater + waterInTruck;
    logger.log('Total Free Water:', totalWater, 'mix design water:', total, 'ice:', iceAdjustment, 'water in truck:', waterInTruck, 'temper water:', temperWater);
    return totalWater;
  }

  getTotalFreeWater(data = {}) {
    return toNumber(convertUnit(this.getTotalWater(data, true), this.getWaterUnit(), this.getUnit()));
  }

  getTotalCement(data = {}) {
    let total = 0;
    let unitSymbol = this.getUnit();

    if (data.results) {
      data.results.forEach(v => {
        if (v.type === ConstituentType.CEMENT) {
          if (unitSymbol !== v.unitSymbol && v.unitSymbol) {
            total += convertUnit(v.actual, v.unitSymbol, unitSymbol);
          } else {
            total += Number(v.actual);
          }
        }
      });
    }

    return toNumber(total);
  }

  getDesignWater(data = {}) {
    let water = 0;

    if (data.results) {
      data.results.forEach(v => {
        if (v.type === ConstituentType.WATER) {
          water += toNumber(convertUnit(v.mix, this.getUnitSymbol(v.unitId), this.getWaterUnit()), 20);
        }
      });
    }

    logger.log('Design Water:', water);
    return water;
  }

  getTotalLoad(data = {}, withWaterInTruck = false) {
    let total = 0;

    if (data.results) {
      data.results.forEach(v => {
        if (v.type !== ConstituentType.ADMIXTURE) {
          total += convertUnit(v.actual, this.getUnitSymbol(v.unitId), this.getUnit());
        }
      });
    }

    if (withWaterInTruck && data.waterInTruck) {
      total += convertUnit(data.waterInTruck, this.getWaterUnit(), this.getUnit());
    }

    return toNumber(total);
  }

  getWaterCementRatio(data = {}, isDesign = false) {
    let waterQty = isDesign ? this.getDesignWater(data) : this.getTotalWater(data, true);
    let cementUnit, cementQty = 0;

    if (data.results) {
      data.results.forEach(v => {
        if (this.materialStore.isCement(v.itemId) || this.materialStore.isCementitious(v.itemId)) {
          cementQty += isDesign ? v.mix : v.actual;
          cementUnit = this.getUnitSymbol(v.unitId);
        }
      });
    }

    if (!cementQty || !cementUnit) {
      return 0;
    }

    waterQty = convertUnit(waterQty, this.getWaterUnit(), cementUnit);

    return waterQty / cementQty;
  }

  getCalculatedItem(item, i, perDrop = false) {
    const { materialStore, batchStore, devicesStore } = this.rootStore;
    const material = item.batchCode ? materialStore.getMaterialByBatchCode(item.batchCode) : materialStore.getMaterialByItemId(item.constituentItemId);
    const md = material ? materialStore.materialDetails[material.materialDetailId] : null;
    const type = material ? materialStore.getConstituentType(material.itemId) : '';
    const vendor = md?.vendorId ? materialStore.vendor.find(v => v.id === md.vendorId) || {} : {};
    const order = i+1;

    if (!type || !md || !(item.actualWeight || item.quantity)) {
      return null;
    }

    let binIds = material.bins ? material.bins.filter(v => v.isActive).map(v => v.binId) : [];
    binIds = material.scaleId && material.bins ? material.bins.map(v => v.binId) : binIds;
    const materialPrimaryBin = material.bins?.find(v => v.isActive && v.isPrimary);
    const primaryBin = devicesStore.getDeviceById(materialPrimaryBin?.binId);
    const isAutoTuning = !!primaryBin?.autoTuning;
    const isDynamicFreeFall = !!primaryBin?.dynamicFreeFall;
    const isAutoJog = !!primaryBin?.autoJog;
    let isManuallyAdded = false;
    const pumpId = material.pumpId;
    const displayName = material ? materialStore.getMaterialNameByItemId(material.itemId) : '';
    const target = perDrop ? item.targetPerDrop : item.quantityBatchSize;
    let actual = perDrop ? item.actualPerDrop : item.actualWeight || 0;
    if (md.batchMode === BatchMode.Skip) {
      actual = target;
      item.manualWeight = target;
      isManuallyAdded = true;
    }
    const tolerance = batchStore.getTolerance(md, target);
    const scale = this.getScaleFromMaterial(material.itemId);
    const hasIndicatorError = scale && Number(batchStore.getScaleWeight(scale)) === 16777216;

    logger.log('calculate drop', material.itemId, displayName, type, actual, item);

    return {
      itemId: material.itemId,
      name: material.code,
      displayName,
      batchCode: item.batchCode,
      unitId: md?.itemBatchUnitId ?? item.unitId,
      unitSymbol: md?.itemBatchUnitSymbol ?? this.getUnitSymbol(item.unitId),
      targetWater: toNumber(this.getTargetWater(item), 2),
      targetWaterLbKg: this.getTargetWaterLbKg(item),
      actualWater: toNumber(this.getActualWater({ ...item, actual }), 2),
      actualFreeWater: toNumber(this.getActualFreeWater({ ...item, actual }), 0),
      waterPercent: this.getWaterPercent(material),
      mix: item.quantity,
      target,
      targetPerDrop: item.targetPerDrop || 0,
      actualPerDrop: item.actualPerDrop || 0,
      high: toNumber(target + tolerance?.over, 2),
      low: toNumber(target - tolerance?.under, 2),
      actual,
      manual: item.manualWeight || 0,
      manuallyTerminated: item.manuallyTerminated || false,
      isManuallyAdded,
      trim: item.trim || 0,
      moisture: item.moisture || 0,
      absorption: md ? md.absorption : 0,
      source: vendor.code || '',
      startTare: item.startTare || 0,
      dischTare: item.dischTare || 0,
      type,
      binIds,
      pumpId,
      order,
      isAutoTuning,
      isDynamicFreeFall,
      isAutoJog,
      hasIndicatorError, // TODO: If more errors are needed, consider changing this to an array of errors
    };
  }

  getResultsFromMixDesign(data) {
    if (!data?.mixDesign?.length) {
      return [];
    }

    let results = [];
    if (!data.results) {
      data.results = [];
    }

    data.mixDesign.forEach((item, i) => {
      const { materialStore } = this.rootStore;
      const material = item.batchCode
        ? materialStore.getMaterialByBatchCode(item.batchCode)
        : materialStore.getMaterialByItemId(item.constituentItemId);

      if (material && !data.results.some(v => v.itemId === material.itemId)) {
        const calculatedItem = this.getCalculatedItem(item, i);
        if (calculatedItem) {
          results.push(calculatedItem);
        }
      }
    });

    return results;
  }

  addItemsAddedManually(data) {
    let results = [];
    let batchSize = data.batchSize || 1;

    if (!data.itemsToBeManuallyAdded?.length) {
      return results;
    }

    data.itemsToBeManuallyAdded.forEach((item) => {
      if (data.results?.find(v => v.itemId === item.constituentItemId)) {
        logger.log('item manually added already in the results', item);
      } else {
        const qty = item.quantity * batchSize;
        results.push({
          itemId: item.constituentItemId,
          name: item.constituentItemCode,
          displayName: this.materialStore.getMaterialNameByItemId(item.constituentItemId),
          unitId: item.unitId,
          unitSymbol: item.unitSymbol,
          mix: item.quantity,
          target: qty,
          actual: qty,
          manual: qty,
          trim: 0,
          type: this.materialStore.getConstituentType(item.constituentItemId) || ConstituentType.UNKNOWN,
          isManuallyAdded: true,
        });
        this.updateInventoryForManuallyAddedItem(item.constituentItemId, qty, data.tId);
      }
    });

    return results;
  }

  updateInventoryForManuallyAddedItem(itemId, quantity, ticketId) {
    const { batchStore, materialStore, devicesStore } = this.rootStore;

    if (batchStore.canReduceInventory && quantity > 0) {
      const material = materialStore.getMaterialByItemId(itemId, true);

      if (material) {
        const md = materialStore.materialDetails[material.materialDetailId];
        const primaryBin = devicesStore.getPrimaryBin(material);
        const binIds = [];

        if (primaryBin) {
          binIds.push(primaryBin.id);
        }

        if (
          md?.itemInventoryUnitSymbol
          && md?.itemBatchUnitSymbol
          && md?.itemInventoryUnitSymbol !== md?.itemBatchUnitSymbol
        ) {
          quantity = toNumber(convertUnit(quantity, md.itemBatchUnitSymbol, md.itemInventoryUnitSymbol), 4);
        }

        materialStore.updateMaterialInventoryUse(material, {
          quantity,
          comment: 'Manual Add',
          binIds,
          ticket: ticketId,
        });
      }
    }
  }

  convertDrops(batchDrops = {}) {
    if (!batchDrops || !Object.keys(batchDrops).length) {
      return [];
    }

    let drops = [];

    Object.keys(batchDrops).forEach((dropNumber) => {
      const drop = batchDrops[dropNumber];

      if (Array.isArray(drop)) {
        drop.forEach((item, i) => {
          const calculatedItem = this.getCalculatedItem({ ...item }, i, true);

          if (calculatedItem) {
            drops.push({ dropNumber: Number(dropNumber), ...calculatedItem });
          }
        });
      }

    });

    logger.log('drops:', drops);

    return drops;
  }

  convertToBatchDrops(drops = []) {
    let batchDrops = {};

    if (!Array.isArray(drops)) {
      return batchDrops;
    }

    drops.forEach(d => {
      if (!batchDrops[d.dropNumber]) {
        batchDrops[d.dropNumber] = [];
      }

      this.addInfoSigns(d);
      batchDrops[d.dropNumber].push(d);
    });

    logger.log('batch drops:', batchDrops);

    return batchDrops;
  }

  calculateTicketData(data, netWeight = true) {
    const { settingsStore, materialStore, locationsStore, batchStore } = this.rootStore;
    const { configuration } = settingsStore;
    let results = [];
    const waterUnit = this.getWaterUnit();

    if (data?.results?.length) {
      data.results.forEach(v => {
        const material = v.batchCode ? materialStore.getMaterialByBatchCode(v.batchCode) : materialStore.getMaterialByItemId(v.itemId);
        const md = material ? materialStore.materialDetails[material.materialDetailId] : null;
        const type = v.type || (material ? materialStore.getConstituentType(material.itemId) : '');
        const displayName = material ? materialStore.getMaterialNameByItemId(material.itemId) : v.displayName;
        const actualDrop = data.mixDesign?.find(d => (d.batchCode && d.batchCode === v.batchCode) || d.constituentItemId === v.itemId);
        const actual = (v.actual || 0) + (actualDrop?.actualWeight || 0);
        const target = actualDrop?.quantityBatchSize ?? v.target;
        const tolerance = batchStore.getTolerance(md, target);
        const moisture = data.batchDrops && type === ConstituentType.AGGREGATE && md?.liveMoisture
          ? getAverageMoisture(data, material?.itemId, this.getUnit())
          : v.moisture;
        const low = actualDrop && md ? toNumber(target - tolerance?.under, 2) : v.low;
        const high = actualDrop && md ? toNumber(target + tolerance?.over, 2) : v.high;

        logger.log('calculate ticket', displayName, v.actual, actualDrop?.actualWeight, actual, actualDrop, low, high);

        if (actual || v.mix) {
          results.push({
            ...v,
            target,
            actual,
            moisture,
            low,
            high,
            itemId: v.itemId || material?.itemId,
            name: v.name || material?.code,
            displayName,
            unitSymbol: v.unitSymbol || this.getUnitSymbol(v.unitId),
            actualWater: v.actualWater || toNumber(this.getActualWater({ ...v, actual }), 2),
            targetWater: toNumber(this.getTargetWater(v), 2),
            targetWaterLbKg: this.getTargetWaterLbKg(v),
            freeMst: this.getFreeMsc(v),
            ssd: this.getSsd(v),
            actualFreeWater: v.actualFreeWater || toNumber(this.getActualFreeWater({ ...v, actual }), 0),
            waterPercent: v.waterPercent || this.getWaterPercent(material),
            type,
          });
        }
      });
    }

    if (data?.mixDesign?.length) {
      results = [ ...results, ...this.getResultsFromMixDesign(data),  ...this.addItemsAddedManually(data) ];
    }

    results = this.calculateCumulative(results, netWeight);
    results = this.checkTare(results);
    results = this.sortResults(results);

    const ticketSubTotal = data.totalQuantityPrice
      ? toNumber(data.totalQuantityPrice, 2)
      : toNumber(this.getSubTotal(data), 2);
    const ticketTaxTotal = data.totalQuantityTax
      ? toNumber(data.totalQuantityTax, 2)
      : toNumber(this.getTaxTotal(data), 2);
    const ticketTotal = toNumber(ticketSubTotal + ticketTaxTotal, 2);
    const batchTime = this.getBatchTime(data);
    const totalLoad = this.getTotalLoad(data);
    const totalLoadWithWaterInTruck = this.getTotalLoad(data, true);
    const totalWater = toNumber(this.getTotalWater(data), 2);
    const totalFreeWater = this.getTotalFreeWater(data);
    const totalCement = this.getTotalCement(data);
    const waterCementRatio = this.getWaterCementRatio(data);
    const designWaterCementRatio = this.getWaterCementRatio(data, true);
    const waterAllowedByDesign = this.getWaterAllowedByDesign(data);
    const waterAllowedToAddByMix = this.getWaterAllowedToAddByMix(data);
    const waterAllowedToAdd = this.getWaterAllowedToAdd(data);
    const mix = data.products.find(v => v.isMix);
    const dropsByNumber = (data.drops || []).reduce((byDrop, item) => {
      if (!byDrop[item.dropNumber]) {
        byDrop[item.dropNumber] = [];
      }
      byDrop[item.dropNumber].push({ ...item });
      return byDrop;
    }, {});
    const batchDrops = this.convertToBatchDrops(
      Object.values(dropsByNumber).reduce((drops, drop) => [
        ...drops,
        ...this.sortResults(this.calculateCumulative(drop, netWeight)),
      ], []),
    );

    return {
      id: data.id,
      code: data.code,
      number: data.number,
      batchOrderId: data.batchOrderId,
      batchOrderCode: data.batchOrderCode,
      dispatchOrderCode: data.dispatchOrderCode,
      dispatchTicketId: data.dispatchTicketId,
      dispatchTicketCode: data.dispatchTicketCode,
      dispatchOrderJobNumber: data.dispatchOrderJobNumber,
      loadStartTime: data.loadStartTime,
      loadEndTime: data.loadEndTime,
      loadEndTime15: data.loadEndTime ? moment(data.loadEndTime).add(15, 'minutes').toISOString() : '',
      loadEndTime20: data.loadEndTime ? moment(data.loadEndTime).add(20, 'minutes').toISOString() : '',
      loadEndTime30: data.loadEndTime ? moment(data.loadEndTime).add(30, 'minutes').toISOString() : '',
      plantId: data.plantId,
      plantName: locationsStore.currentPlant.description,
      plantCode: locationsStore.currentPlant.code,
      locationName: locationsStore.currentLocation.name,
      projectId: data.projectId,
      projectCode: data.projectCode,
      projectName: data.projectName,
      customerId: data.customerId,
      customerCode: data.customerCode,
      customerName: data.customerName,
      customerAddress: data.customerAddress,
      accountType: data.accountType,
      shipToAddress: data.shipToAddress,
      purchaseOrderNumber: data.purchaseOrderNumber,
      usage: data.usage,
      truckId: data.truckId,
      truckCode: data.truckCode,
      driverId: data.driverId,
      driverCode: data.driverCode,
      driverName: data.driverName,
      taxRate: data.taxRate,
      taxCodeCode: data.taxCodeCode,
      isVoided: data.isVoided,
      mixSlump: data.mixSlump,
      orderMixQuantity: data.orderMixQuantity,
      orderDeliveredToDateMixQuantity: data.orderDeliveredToDateMixQuantity || data.mixQuantity * (data.orderLoadNumber || 1),
      orderDeliveredToDatePriceWithTax: data.orderDeliveredToDatePriceWithTax || 0,
      printWithoutWeights: !!data.printWithoutWeights,
      printWithoutWeightsAtEnd: !!data.printWithoutWeightsAtEnd,
      printWithWeights: !!data.printWithWeights,
      printOnlyWeights: !!data.printOnlyWeights,
      products: data.products,
      mixDesign: data.mixDesign || [],
      mixDesignCode: data.mixDesignCode,
      orderLoadNumber: data.orderLoadNumber || 1,
      results,
      drops: data.drops || [],
      dropTimes: data.dropTimes || [],
      dropTimesMap: Object.fromEntries(
        (data.dropTimes || []).map(({ dropNumber, loadStartTime, loadEndTime }) => [
          dropNumber,
          {
            loadStartTime: displayDate(loadStartTime, this.timezone, 'h:mm:ss A'),
            loadEndTime: displayDate(loadEndTime, this.timezone, 'h:mm:ss A'),
            loadTime: this.getBatchTime(data.dropTimes.find(v => v.dropNumber === dropNumber)),
          },
        ]),
      ),
      batchDrops,
      ticketSubTotal,
      ticketTaxTotal,
      ticketTotal,
      batchTime,
      mixBatchQuantity: data.mixBatchQuantity,
      mixDropQuantity: data.mixDropQuantity,
      mixQuantity: data.dropSize || data.mixQuantity,
      timezone: this.timezone,
      logo: `data:image/png;base64,${this.api.companyConfig.logoFile}`,
      codeAsBarcode: data.code || '',
      codeAsBarcodeImage: generateBarcodeImage(data.code || ''),
      totalLoad,
      totalLoadWithWaterInTruck,
      totalWater,
      totalFreeWater,
      totalCement,
      unit: this.getUnit(),
      waterUnit,
      waterCementRatio,
      designWaterCementRatio,
      waterInTruck: data.waterInTruck || 0,
      adjustedWater: data.adjustedWater || 0,
      manualLoad: !!data.manualLoad,
      showManualLoad: !!configuration.recordManualBatchToTicket,
      onlyWeights: !!data.onlyWeights,
      ice: data.ice || 0,
      iceUnit: data.iceUnit,
      temperWater: data.temperWater || 0,
      temperWaterUnit: data.temperWaterUnitSymbol || this.getWaterUnit(), // required by TT-editor
      temperWaterUnitSymbol: data.temperWaterUnitSymbol || this.getWaterUnit(),
      isAborted: !!data.isAborted,
      isDemo: !!data.isDemo,
      isTestMode: !!data.isTestMode,
      runMode: data.runMode || '',
      printPrices: !!data.printPrices,
      paymentForm: data.paymentForm || '',
      zoneCode: data.zoneCode || '',
      instructions: data.instructions || [],
      waterAllowedByDesign,
      waterAllowedToAdd,
      waterAllowedToAddByMix,
      maxWater: data.maxWater || mix?.maxWater || 0,
      isOffline: data.isOffline || data.batchStartedOffline || false,
      batchStartedOffline: data.batchStartedOffline || false,
      dischargeSequenceId: data.dischargeSequenceId,
      mixingTime: data.mixingTime || null,
      mergeBatchTicketId: data.mergeBatchTicketId,
      lotBlockNumber: data.lotBlockNumber || '',
      orderedBy: data.orderedBy || '',
      onJobDueTime: data.onJobDueTime || '',
      totalQuantityTax: data.totalQuantityTax || 0,
      totalQuantityPrice: data.totalQuantityPrice || 0,
      origin: data.origin,
      dotPlantCode: configuration.dotPlantCode || '',
      mixExternalDescription: mix?.itemExternalDescription || '',
      heatChargeAmt: data.heatChargeAmt || 0,
      heatChargePrt: data.heatChargePrt || '',
      minimumLoadChargeAmt: data.minimumLoadChargeAmt || 0,
      minimumLoadChargePrt: data.minimumLoadChargePrt || '',
      events: data.events || [],
    };
  }

  sortResults(results) {
    if (!Array.isArray(results)) {
      return results;
    }

    const typeOrder = [
      ConstituentType.AGGREGATE,
      ConstituentType.CEMENT,
      ConstituentType.ADMIXTURE,
      ConstituentType.WATER,
      ConstituentType.UNKNOWN,
    ];

    return results.sort((a, b) => typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type));
  }

  addInfoSigns(item) {
    let infoSigns = '';

    if (item.manuallyTerminated) {
      infoSigns += '^';
    }
    if (item.low && (item.actual > item.high || item.actual < item.low)) {
      infoSigns += '*';
    }

    item.infoSigns = infoSigns;
  }

  checkTare(results) {
    let arr = [ ...results ];
    let types = {};

    arr.forEach((v) => {
      this.addInfoSigns(v);

      if ([ConstituentType.CEMENT, ConstituentType.AGGREGATE].includes(v.type)) {
        if (types[v.type]) {
          v.startTare = 0;
          v.dischTare = 0;
        } else if (v.target) {
          types[v.type] = v.type;
          v.isScaleTare = true;
          const scale = this.getScaleFromMaterial(v.itemId);
          v.isStartTareOverTolerance = v.startTare > scale?.startTare || v.startTare < 0 - scale?.startTare;
          v.isDischTareOverTolerance = v.dischTare > scale?.dischargeTare || v.dischTare < 0 - scale?.dischargeTare;
        }
      }
      if ([ConstituentType.WATER, ConstituentType.ADMIXTURE].includes(v.type)) {
        v.startTare = 0;
        v.dischTare = 0;
      }
    });

    return arr;
  }

  getScaleFromMaterial(itemId) {
    const { materialStore, devicesStore } = this.rootStore;
    const material = materialStore.getMaterialByItemId(itemId);

    if (material?.scaleId) {
      return devicesStore.getDeviceById(material.scaleId);
    }

    const primaryBin = devicesStore.getPrimaryBin(material);
    return devicesStore.getDeviceById(primaryBin?.scaleId);
  }

  calculateCumulative(results, netWeight = true) {
    if (!results) {
      return [];
    }

    const { settingsStore } = this.rootStore;
    const { configuration } = settingsStore;
    let arr = [ ...results ];

    if (netWeight || !configuration.toleranceMode || configuration.toleranceMode === ToleranceMode.NetWeight) {
      return results;
    }

    if (configuration.toleranceMode === ToleranceMode.NYDOTCumulative) {
      arr.sort((a, b) => {
        if (a.type < b.type) {
          return -1;
        }
        if (a.type > b.type) {
          return 1;
        }
        return a.order - b.order;
      });

      arr.forEach((v, i) => {
        const prev = arr[i - 1];
        if (prev && prev.type === v.type && [ConstituentType.CEMENT, ConstituentType.AGGREGATE].includes(v.type)) {
          v.netTarget = v.netTarget || v.target;
          v.netActual = v.netActual || v.actual;
          v.netLow = v.netLow || v.low;
          v.netHigh = v.netHigh || v.high;

          v.cumulativeTarget = v.netTarget + (prev.cumulativeTarget ? prev.cumulativeTarget : prev.target);
          v.cumulativeActual = v.netActual + (prev.cumulativeActual ? prev.cumulativeActual : prev.actual);
          v.cumulativeLow = v.netLow + (prev.cumulativeLow ? prev.cumulativeLow : prev.low);
          v.cumulativeHigh = v.netHigh + (prev.cumulativeHigh ? prev.cumulativeHigh : prev.high);

          v.target = v.cumulativeTarget;
          v.actual = v.cumulativeActual;
          v.low = v.cumulativeLow;
          v.high = v.cumulativeHigh;

          v.startTare = '';
          v.dischTare = '';
        }
        if ([ConstituentType.WATER, ConstituentType.ADMIXTURE].includes(v.type)) {
          v.startTare = '';
          v.dischTare = '';
        }
      });
    }

    return arr;
  }

  @action
  loadDrafts() {
    const url = '/api/ticket/template/draft';
    return this.api
      .makeAuthorizedRequest(url)
      .then(resp => {
        this.templates = resp || [];
      });
  };

  @action
  getDraft() {
    return this.api
      .makeAuthorizedRequest(`/api/ticket/template/draft/${this.templateId}`)
      .then(resp => {
        this.template = resp && resp.template ? resp.template : getTicketTemplate();
        return resp;
      });
  };

  getTemplatesDetails() {
    const start = async () => {
      await asyncForEach(this.templates, async (data, i) => {
        this.getTemplate(data.id);
        logger.debug('load template', i, data.id);
        await waitFor(200);
      });
    };

    return start();
  }

  @action
  loadTemplates(withDetails = true) {
    const scope = this.rootStore.locationsStore.divisionCode;

    if (!scope) {
      return Promise.reject();
    }

    const url = '/api/ticket/template';
    return this.api
      .makeAuthorizedRequest(url, { scope })
      .then(resp => {
        this.templates = resp || [];
        const templateId = this.api.storage.getItem('ticketTemplateId');
        this.numberOfCopies = this.api.storage.getItem('numberOfCopies') || 1;

        if (templateId && this.templates.find(v => v.id === Number(templateId))) {
          this.templateId = templateId;
        }

        if (withDetails) {
          this.getTemplatesDetails();
        }
      });
  };

  getTermsTemplate() {
    const { configuration } = this.rootStore.settingsStore;
    if (!this.templates.length || (!configuration?.termsAndConditionsTemplateId)) {
      return '';
    }

    const tpl =  this.templates.find(v => v.id === Number(configuration.termsAndConditionsTemplateId));
    this.termsTemplate = tpl ? tpl.template : '';

    return this.termsTemplate;

  }

  @action
  getTemplate(templateId = this.templateId) {
    if (this.templates.length) {
      const t = this.templates.find(v => v.id === Number(templateId));

      if (t && t.template) {
        this.template = t.template;
        return Promise.resolve(t.template);
      }
    }

    return this.api
      .makeAuthorizedRequest(`/api/ticket/template/${templateId}`)
      .then(resp => {
        this.template = resp && resp.template ? resp.template : getTicketTemplate();
        const templateIndex = this.templates.findIndex(v => v.id === Number(templateId));

        if (templateIndex > -1) {
          this.templates[templateIndex].template = resp.template;
        }

        return this.template;
      });
  };

  startWebSockets = () => {
    const { websocketStore, connectionStore } = this.rootStore;
    this.ws = websocketStore.connection;
    clearTimeout(this.wsTimer);

    if (!this.ws || !connectionStore.isOnline || !websocketStore.isTrulyConnected) {
      this.wsTimer = setTimeout(() => {
        this.startWebSockets();
      }, 2000);
      return;
    }

    this.ws.on(WebsocketEvent.TICKET_TEMPLATE_ADD, (data) => {
      logger.log('ws:', WebsocketEvent.TICKET_TEMPLATE_ADD, data);
      this.templates.push(data);
    });

    this.ws.on(WebsocketEvent.TICKET_TEMPLATE_UPDATE, (data) => {
      logger.log('ws:', WebsocketEvent.TICKET_TEMPLATE_UPDATE, data);
      const index = this.templates.findIndex(v => v.id === data.id);

      if (index > -1) {
        this.templates[index] = Object.assign(this.templates[index], data);
      }
    });

    this.ws.on(WebsocketEvent.TICKET_TEMPLATE_DELETE, (data) => {
      logger.log('ws:', WebsocketEvent.TICKET_TEMPLATE_DELETE, data);
      this.templates = this.templates.filter(v => v.id !== data.id);
    });

    this.ws.on(WebsocketEvent.BATCH_TICKET_UPDATE, (data) => {
      logger.log('ws:', WebsocketEvent.BATCH_TICKET_UPDATE, data);
      this.loadTickets();
    });

    this.ws.on(WebsocketEvent.BATCH_TICKET_DELETE, (data) => {
      logger.log('ws:', WebsocketEvent.BATCH_TICKET_DELETE, data);
      this.loadTickets();
    });

    this.ws.onclose(() => {
      this.startWebSockets();
    });
  };

}

export default TicketStore;
